/**
 * Sellable overlay: annotates core InvenTree tables with the Shopify-sellable
 * quantity next to the native stock value.
 *
 * Injected into the SPA shell by QuickFilterScriptMiddleware. Pure DOM work;
 * fails silently if InvenTree markup changes.
 *
 * Row identification: core tables navigate via click handlers (no <a> links),
 * so rows carry no part id in the DOM. Instead we observe the SPA's own list
 * API responses (XHR) and match table rows to records BY POSITION - the table
 * renders exactly what the last matching list call returned, in order.
 */

const BADGE_ATTR = 'data-avx-sellable';
const API_URL = '/plugin/atlavox-shopify/api/sellable/';
const FLAGS_URL = '/plugin/atlavox-shopify/api/bom-flags/';
const REFRESH_MS = 60_000;

type SellableEntry = {
  sellable: number;
  incoming?: number;
  velocity?: number;
  days?: number | null;
  sku: string;
};

let sellableMap: Record<string, SellableEntry> | null = null;
// BOM line pks whose limits_availability flag is False ('soft' lines).
let softLines: Set<number> | null = null;
let lastFetch = 0;
let fetching: Promise<void> | null = null;

type PartFlags = { assembly: boolean; component: boolean; salable: boolean };

// Latest list results per dataset, captured from the SPA's own XHR traffic.
// Each entry maps a table row (by position) to a part id; the BOM dataset
// also carries the BomItem pk per row (for the limits-availability toggle),
// and the parts dataset carries the attribute flags for the icon cluster.
const datasets: { name: string; partIds: number[]; bomPks?: number[]; flags?: PartFlags[] }[] = [
  { name: 'parts', partIds: [], flags: [] }, // /api/part/  -> record.pk
  { name: 'soLines', partIds: [] }, // /api/order/so-line/ -> record.part
  { name: 'bom', partIds: [], bomPks: [] } // /api/bom/  -> record.sub_part
];

function captureResponse(url: string, body: string): void {
  let results: Record<string, unknown>[] = [];
  try {
    const parsed = JSON.parse(body) as { results?: Record<string, unknown>[] };
    results = Array.isArray(parsed) ? (parsed as Record<string, unknown>[]) : (parsed.results ?? []);
  } catch {
    return;
  }
  if (!Array.isArray(results)) return;

  const path = url.split('?')[0];
  if (/\/api\/part\/$/.test(path)) {
    datasets[0].partIds = results.map((r) => Number(r.pk));
    datasets[0].flags = results.map((r) => ({
      assembly: Boolean(r.assembly),
      component: Boolean(r.component),
      salable: Boolean(r.salable)
    }));
  } else if (/\/api\/order\/so-line\/$/.test(path)) {
    datasets[1].partIds = results.map((r) => Number(r.part));
  } else if (/\/api\/bom\/$/.test(path)) {
    datasets[2].partIds = results.map((r) => Number(r.sub_part));
    datasets[2].bomPks = results.map((r) => Number(r.pk));
  } else {
    return;
  }
  scan();
}

function csrfToken(): string {
  const match = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : '';
}

// Observe the SPA's XHR traffic (axios uses XMLHttpRequest)
const origOpen = XMLHttpRequest.prototype.open;
XMLHttpRequest.prototype.open = function (
  this: XMLHttpRequest & { __avxUrl?: string },
  method: string,
  url: string | URL,
  ...rest: unknown[]
) {
  this.__avxUrl = String(url);
  this.addEventListener('load', () => {
    try {
      if (this.__avxUrl && typeof this.responseText === 'string') {
        captureResponse(this.__avxUrl, this.responseText);
      }
    } catch {
      /* never break the app */
    }
  });
  // @ts-expect-error passthrough of variadic args
  return origOpen.call(this, method, url, ...rest);
};

async function loadMap(): Promise<void> {
  if (sellableMap && Date.now() - lastFetch < REFRESH_MS) return;
  if (fetching) return fetching;

  fetching = (async () => {
    try {
      const [mapResponse, flagsResponse] = await Promise.all([
        fetch(API_URL, { credentials: 'include', headers: { Accept: 'application/json' } }),
        fetch(FLAGS_URL, { credentials: 'include', headers: { Accept: 'application/json' } })
      ]);
      if (mapResponse.ok) {
        const body = (await mapResponse.json()) as { parts?: Record<string, SellableEntry> };
        if (body.parts) {
          sellableMap = body.parts;
          lastFetch = Date.now();
          console.debug('[avx-sellable] map loaded:', Object.keys(body.parts).length, 'parts');
        }
      } else {
        console.debug('[avx-sellable] map fetch failed:', mapResponse.status);
      }
      if (flagsResponse.ok) {
        const flags = (await flagsResponse.json()) as { soft?: number[] };
        softLines = new Set(flags.soft ?? []);
      }
    } catch (exc) {
      console.debug('[avx-sellable] map fetch error', exc);
    } finally {
      fetching = null;
    }
  })();
  return fetching;
}

async function toggleSoftLine(bomPk: number, limits: boolean): Promise<void> {
  // Optimistic: flip locally first so rescans render the new state
  if (!softLines) softLines = new Set();
  const wasSoft = softLines.has(bomPk);
  if (limits) softLines.delete(bomPk);
  else softLines.add(bomPk);

  try {
    const response = await fetch(`${FLAGS_URL}${bomPk}/`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrfToken() },
      body: JSON.stringify({ limits_availability: limits })
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    // Sellable numbers shift when a line starts/stops constraining
    lastFetch = 0;
    scan();
  } catch (exc) {
    console.warn('[avx-sellable] limits toggle failed', exc);
    if (wasSoft) softLines.add(bomPk);
    else softLines.delete(bomPk);
    scan();
  }
}

// Header words that identify a table we know how to map to a dataset
const TABLE_HINTS: { name: string; hints: string[] }[] = [
  { name: 'parts', hints: ['ipn', 'total stock'] },
  { name: 'soLines', hints: ['allocated', 'shipped'] },
  { name: 'bom', hints: ['component', 'can build'] }
];

function identifyDataset(headerTexts: string[], rowCount: number) {
  // Header-signature match ONLY - a row-count coincidence must never inject
  // into unrelated tables (e.g. the Sales Orders list).
  for (const { name, hints } of TABLE_HINTS) {
    if (hints.some((h) => headerTexts.some((t) => t.startsWith(h)))) {
      const dataset = datasets.find((d) => d.name === name);
      if (dataset && dataset.partIds.length === rowCount && rowCount > 0) return dataset;
    }
  }
  return null;
}

function teardown(table: HTMLTableElement): void {
  for (const el of Array.from(table.querySelectorAll(`[${BADGE_ATTR}]`))) {
    el.remove();
  }
}

// ----------------------------------------------------------------------
// Part attribute icons (parts table only): three FIXED slots to the left
// of the thumbnail - assembly (tools), component (grid dots), saleable
// (Shopify-green bag). Empty slots keep thumbnails/names aligned.
// ----------------------------------------------------------------------

const ICON_SIZE = 15;

function svgIcon(inner: string, color: string, filled = false): string {
  const paint = filled
    ? `fill="${color}" stroke="none"`
    : `fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"`;
  return (
    `<svg width="${ICON_SIZE}" height="${ICON_SIZE}" viewBox="0 0 24 24" ${paint} ` +
    `xmlns="http://www.w3.org/2000/svg">${inner}</svg>`
  );
}

// Tabler 'tools' - the same glyph InvenTree uses for Assembled Part
const ICON_ASSEMBLY = svgIcon(
  '<path d="M3 21h4l13 -13a1.5 1.5 0 0 0 -4 -4l-13 13v4" /><path d="M14.5 5.5l4 4" />' +
    '<path d="M12 8l-5 -5l-4 4l5 5" /><path d="M7 8l-1.5 1.5" />' +
    '<path d="M16 12l5 5l-4 4l-5 -5" /><path d="M16 17l-1.5 1.5" />',
  '#b8bcc4'
);

// Tabler 'grid-dots' - the same glyph InvenTree uses for Component Part
const ICON_COMPONENT = svgIcon(
  [5, 12, 19]
    .flatMap((y) => [5, 12, 19].map((x) => `<circle cx="${x}" cy="${y}" r="1.6" />`))
    .join(''),
  '#b8bcc4',
  true
);

// The official Shopify logo glyph (Simple Icons 'shopify': bag with the S)
const ICON_SALABLE = svgIcon(
  '<path fill-rule="evenodd" d="M15.337 23.979l7.216-1.561s-2.604-17.613-2.625-17.73c-.018-.116-.114-.192-.211-.192s-1.929-.136-1.929-.136-1.275-1.274-1.439-1.411c-.045-.037-.075-.057-.121-.074l-.914 21.104h.023zM11.71 11.305s-.81-.424-1.774-.424c-1.447 0-1.504.906-1.504 1.141 0 1.232 3.24 1.715 3.24 4.629 0 2.295-1.44 3.76-3.406 3.76-2.354 0-3.54-1.465-3.54-1.465l.646-2.086s1.245 1.066 2.28 1.066c.675 0 .975-.545.975-.932 0-1.619-2.654-1.694-2.654-4.359-.034-2.237 1.571-4.416 4.827-4.416 1.257 0 1.875.361 1.875.361l-.945 2.715-.02.01zM11.17.83c.136 0 .271.038.405.135-.984.465-2.064 1.639-2.508 3.992-.656.213-1.293.405-1.889.578C7.697 3.75 8.951.84 11.17.84V.83zm1.235 2.949v.135c-.754.232-1.583.484-2.394.736.466-1.777 1.333-2.645 2.085-2.971.193.501.309 1.176.309 2.1zm.539-2.234c.694.074 1.141.867 1.429 1.755-.349.114-.735.231-1.158.366v-.252c0-.752-.096-1.371-.271-1.871v.002zm2.992 1.289c-.02 0-.06.021-.078.021s-.289.075-.714.21c-.423-1.233-1.176-2.37-2.508-2.37h-.115C12.135.209 11.669 0 11.265 0 8.159 0 6.675 3.877 6.21 5.846c-1.194.365-2.063.636-2.16.674-.675.213-.694.232-.772.87-.075.462-1.83 14.063-1.83 14.063L15.009 24l.927-21.166z" />',
  '#95bf47',
  true
);

const FLAG_SLOTS: { key: keyof PartFlags; icon: string; label: string }[] = [
  { key: 'assembly', icon: ICON_ASSEMBLY, label: 'Assembled part' },
  { key: 'component', icon: ICON_COMPONENT, label: 'Component part' },
  { key: 'salable', icon: ICON_SALABLE, label: 'Saleable (Shopify)' }
];

function buildFlagCluster(flags?: PartFlags): HTMLSpanElement {
  const cluster = document.createElement('span');
  cluster.style.cssText =
    `display:inline-flex;gap:2px;vertical-align:middle;` +
    `flex:none;width:${(ICON_SIZE + 2) * FLAG_SLOTS.length}px;`;

  for (const slot of FLAG_SLOTS) {
    const holder = document.createElement('span');
    holder.style.cssText =
      `display:inline-flex;width:${ICON_SIZE}px;height:${ICON_SIZE}px;` +
      'align-items:center;justify-content:center;flex:none;';
    if (flags && flags[slot.key]) {
      holder.innerHTML = slot.icon; // static, locally-authored SVG only
      holder.title = slot.label;
    }
    cluster.appendChild(holder);
  }
  return cluster;
}

function annotateTable(table: HTMLTableElement): void {
  // Tables rendered by our own panel manage their columns natively (and
  // support user sorting, which would break position-based injection).
  if (table.hasAttribute('data-avx-owned')) return;

  // ALWAYS clean up first - even when we cannot (re)inject, a previous
  // pass's leftover header cell would skew every column by one.
  teardown(table);

  if (!sellableMap) return;

  const headerRow = table.querySelector<HTMLTableRowElement>('thead tr');
  if (!headerRow) return;

  const headerTexts = Array.from(headerRow.cells).map((th) =>
    (th.textContent || '').trim().toLowerCase()
  );
  const rows = Array.from(table.querySelectorAll<HTMLTableRowElement>('tbody tr'));
  const dataset = identifyDataset(headerTexts, rows.length);
  if (!dataset) return;

  // Insert right after the IPN column (or the first labeled column) - the
  // trailing actions column is sticky-pinned and overlaps appended cells.
  const ipnIndex = headerTexts.findIndex((t) => t === 'ipn');
  const firstLabeled = headerTexts.findIndex((t) => t.length > 0);
  const insertIndex = (ipnIndex >= 0 ? ipnIndex : Math.max(firstLabeled, 0)) + 1;

  function nativeBorder(reference: HTMLElement | null): string {
    if (reference) {
      const cs = getComputedStyle(reference);
      if (cs.borderRightWidth !== '0px' && cs.borderRightStyle !== 'none') return cs.borderRight;
      if (cs.borderLeftWidth !== '0px' && cs.borderLeftStyle !== 'none') return cs.borderLeft;
    }
    return '1px solid rgba(255,255,255,0.12)';
  }

  // Two injected columns, inserted side by side after the IPN column
  const COLUMNS: {
    label: string;
    hint: string;
    color: string;
    value: (entry: SellableEntry) => string;
    dim: (entry: SellableEntry) => boolean;
  }[] = [
    {
      label: 'Sellable',
      hint: 'Shopify sellable: (stock − allocated) + buildable from materials',
      color: '#12b886',
      value: (entry) => String(entry.sellable),
      dim: (entry) => entry.sellable === 0
    },
    {
      label: 'Incoming',
      hint: 'On open purchase orders + buildable once incoming components arrive',
      color: '#228be6',
      value: (entry) => String(entry.incoming ?? 0),
      dim: (entry) => (entry.incoming ?? 0) === 0
    },
    {
      label: 'Days',
      hint: 'Days of stock at current sales velocity (sales-order history, incl. BOM demand)',
      color: '#f59f00',
      value: (entry) => (entry.days === null || entry.days === undefined ? '∞' : String(entry.days)),
      dim: (entry) => entry.days === null || entry.days === undefined
    }
  ];

  // BOM tables additionally get the limits-availability toggle: unchecked
  // lines are still consumed and tracked at fulfilment, but never limit the
  // parent's sellable count.
  const isBom = dataset.name === 'bom' && Array.isArray(dataset.bomPks);
  const injectedCount = COLUMNS.length + (isBom ? 1 : 0);

  // Inherit native header styling (borders, padding, theme) from a sibling
  const refTh = headerRow.cells[insertIndex - 1] ?? headerRow.cells[0];
  COLUMNS.forEach((column, offset) => {
    const th = document.createElement('th');
    th.setAttribute(BADGE_ATTR, '1');
    if (refTh) th.className = refTh.className;
    th.textContent = column.label;
    th.title = column.hint;
    th.style.cssText = `text-align:right;color:${column.color};white-space:nowrap;`;
    th.style.borderRight = nativeBorder(refTh);
    headerRow.insertBefore(th, headerRow.cells[insertIndex + offset] ?? null);
  });
  if (isBom) {
    const th = document.createElement('th');
    th.setAttribute(BADGE_ATTR, '1');
    if (refTh) th.className = refTh.className;
    th.textContent = 'Limits avail.';
    th.title =
      'Checked: this line limits how many assemblies count as sellable. ' +
      'Unchecked: still consumed and tracked at fulfilment, but never gates the parent.';
    th.style.cssText = 'text-align:center;white-space:nowrap;';
    th.style.borderRight = nativeBorder(refTh);
    headerRow.insertBefore(th, headerRow.cells[insertIndex + COLUMNS.length] ?? null);
  }

  // Part-attribute icon column (parts table only): a slim dedicated column
  // immediately LEFT of the Part column, with a fixed three-slot strip per
  // row (assembly / component / saleable). A separate column - rather than
  // injecting inside the part cell - leaves the native thumbnail+name
  // layout completely untouched, so alignment can never break.
  const isParts = dataset.name === 'parts' && Array.isArray(dataset.flags);
  const partColIndex = headerTexts.findIndex((t) => t === 'part');
  const flagsCol = isParts && partColIndex >= 0;

  if (flagsCol) {
    const th = document.createElement('th');
    th.setAttribute(BADGE_ATTR, '1');
    if (refTh) th.className = refTh.className;
    th.textContent = '';
    th.title = 'Part attributes: assembled / component / saleable';
    th.style.cssText = 'white-space:nowrap;width:1%;';
    th.style.borderRight = nativeBorder(refTh);
    headerRow.insertBefore(th, headerRow.cells[partColIndex] ?? null);
  }
  const totalInjected = injectedCount + (flagsCol ? 1 : 0);
  // Value columns computed their positions from the ORIGINAL header
  // indices; the flags column sits before them and shifts everything one.
  const valueShift = flagsCol && partColIndex < insertIndex ? 1 : 0;

  const expectedCells = headerRow.cells.length - totalInjected; // header already has our th's
  rows.forEach((row, index) => {
    // Skip rows mid-React-update (cell count mismatch would skew alignment)
    if (row.cells.length !== expectedCells) return;

    if (flagsCol) {
      const td = document.createElement('td');
      td.setAttribute(BADGE_ATTR, '1');
      const refFlags = row.cells[partColIndex] ?? row.cells[0];
      if (refFlags) td.className = refFlags.className;
      td.style.cssText = 'white-space:nowrap;width:1%;';
      td.style.borderRight = nativeBorder(refFlags);
      td.appendChild(buildFlagCluster(dataset.flags?.[index]));
      row.insertBefore(td, row.cells[partColIndex] ?? null);
    }

    const refTd = row.cells[insertIndex - 1 + valueShift] ?? row.cells[0];
    const entry = sellableMap?.[String(dataset.partIds[index])];

    COLUMNS.forEach((column, offset) => {
      const td = document.createElement('td');
      td.setAttribute(BADGE_ATTR, '1');
      if (refTd) td.className = refTd.className;
      td.style.cssText = `text-align:right;font-weight:600;color:${column.color};white-space:nowrap;`;
      td.style.borderRight = nativeBorder(refTd);

      if (entry) {
        td.textContent = column.value(entry);
        td.title = `SKU ${entry.sku}`;
        if (column.dim(entry)) td.style.opacity = '0.45';
      } else {
        td.textContent = '—';
        td.style.color = 'inherit';
      }

      row.insertBefore(td, row.cells[insertIndex + offset + valueShift] ?? null);
    });

    if (isBom) {
      const bomPk = dataset.bomPks?.[index];
      const td = document.createElement('td');
      td.setAttribute(BADGE_ATTR, '1');
      if (refTd) td.className = refTd.className;
      td.style.cssText = 'text-align:center;';
      td.style.borderRight = nativeBorder(refTd);

      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.style.cssText = 'cursor:pointer;accent-color:#228be6;';
      checkbox.checked = !(bomPk !== undefined && softLines?.has(bomPk));
      checkbox.disabled = bomPk === undefined || softLines === null;
      // Row clicks navigate; keep the toggle from triggering that
      checkbox.addEventListener('click', (event) => event.stopPropagation());
      checkbox.addEventListener('change', (event) => {
        event.stopPropagation();
        if (bomPk !== undefined) {
          void toggleSoftLine(bomPk, (event.target as HTMLInputElement).checked);
        }
      });
      td.addEventListener('click', (event) => event.stopPropagation());

      td.appendChild(checkbox);
      row.insertBefore(td, row.cells[insertIndex + COLUMNS.length + valueShift] ?? null);
    }
  });
}

let scheduled = false;
// ----------------------------------------------------------------------
// Part-detail header chips: SELLABLE (and BUILDABLE for assemblies) next
// to the core IN STOCK / NO STOCK badge. Core's badge only counts finished
// stock, which reads as 'NO STOCK' on phantom assemblies that are in fact
// buildable from components - the chip answers 'how many can I promise /
// build right now' at a glance.
// ----------------------------------------------------------------------

const HEADER_ATTR = 'data-avx-header-chip';

type HeaderData = { sellable: number; buildable: number; assembly: boolean };
let headerCache: { pid: number; at: number; data: HeaderData } | null = null;
let headerFetch: Promise<void> | null = null;

async function fetchHeaderData(pid: number): Promise<void> {
  if (headerCache && headerCache.pid === pid && Date.now() - headerCache.at < REFRESH_MS) return;
  if (headerFetch) return headerFetch;
  headerFetch = (async () => {
    try {
      const response = await fetch(`/plugin/atlavox-shopify/api/part-status/${pid}/`, {
        credentials: 'include',
        headers: { Accept: 'application/json' }
      });
      if (response.ok) {
        const body = (await response.json()) as {
          sellable?: number;
          buildable?: number;
          assembly?: boolean;
        };
        headerCache = {
          pid,
          at: Date.now(),
          data: {
            sellable: Number(body.sellable ?? 0),
            buildable: Number(body.buildable ?? 0),
            assembly: Boolean(body.assembly)
          }
        };
      }
    } catch {
      /* chip is a nicety - never break the page */
    } finally {
      headerFetch = null;
    }
  })();
  return headerFetch;
}

function annotateHeader(): void {
  const match = window.location.pathname.match(/\/part\/(\d+)/);
  const pid = match ? Number(match[1]) : null;
  const existing = Array.from(document.querySelectorAll(`[${HEADER_ATTR}]`)) as HTMLElement[];

  if (!pid) {
    existing.forEach((el) => el.remove());
    return;
  }
  if (existing.length && existing[0].getAttribute(HEADER_ATTR) !== String(pid)) {
    existing.forEach((el) => el.remove());
  }

  const data = headerCache && headerCache.pid === pid ? headerCache.data : null;
  if (!data) {
    void fetchHeaderData(pid).then(scan);
    return;
  }

  const texts = [`SELLABLE: ${data.sellable}`];
  if (data.assembly) texts.push(`BUILDABLE: ${data.buildable}`);

  const current = Array.from(
    document.querySelectorAll(`[${HEADER_ATTR}="${pid}"]`)
  ) as HTMLElement[];
  if (current.length === texts.length) {
    // Update in place (a no-op when unchanged, so the MutationObserver
    // that re-triggers scan() cannot loop on our own writes)
    current.forEach((el, i) => {
      if (el.textContent !== texts[i]) el.textContent = texts[i];
    });
    return;
  }
  current.forEach((el) => el.remove());

  // Anchor: the core stock badge's label (a childless span/div)
  const leaves = Array.from(document.querySelectorAll('div,span')) as HTMLElement[];
  const anchor = leaves.find(
    (el) =>
      el.childElementCount === 0 &&
      /^(IN STOCK: |NO STOCK$|OUT OF STOCK)/.test((el.textContent || '').trim())
  );
  const badgeRoot = anchor?.parentElement;
  if (!badgeRoot || !badgeRoot.parentElement) return;

  const colors = ['#0ca678', '#1098ad'];
  let ref: Element = badgeRoot;
  texts.forEach((text, i) => {
    const chip = document.createElement('span');
    chip.setAttribute(HEADER_ATTR, String(pid));
    chip.textContent = text;
    chip.style.cssText =
      `background:${colors[i]};color:#fff;font-weight:700;font-size:11px;` +
      'padding:4px 10px;border-radius:16px;margin-left:6px;letter-spacing:.25px;' +
      'white-space:nowrap;align-self:center;';
    ref.insertAdjacentElement('afterend', chip);
    ref = chip;
  });
}

function scan(): void {
  if (scheduled) return;
  scheduled = true;
  window.setTimeout(async () => {
    scheduled = false;
    await loadMap();
    try {
      annotateHeader();
    } catch {
      /* never break the app */
    }
    for (const table of Array.from(document.querySelectorAll('table'))) {
      try {
        // annotateTable tears down first, so stale cells are removed even
        // when the map is unavailable or no dataset matches this table
        annotateTable(table as HTMLTableElement);
      } catch {
        try {
          teardown(table as HTMLTableElement);
        } catch {
          /* cosmetic only */
        }
      }
    }
  }, 250);
}

// Expose a debug handle: window.__avxSellable
(window as unknown as Record<string, unknown>).__avxSellable = {
  datasets,
  map: () => sellableMap,
  rescan: scan
};

const observer = new MutationObserver(scan);
observer.observe(document.body, { childList: true, subtree: true });
scan();

export {};
